/**
 * Phase 9.7.21 — 🏆 Public "Most Pushed Today" Leaderboard
 * --------------------------------------------------------
 * Route: /leaderboard/today (public, no auth)
 * Top 25 artists by 24h push momentum + animated podium for top 3.
 * Each row has a "Share Placement" button that exports a branded PNG.
 *
 * Analytics-integrity contract preserved:
 *   ▶ Plays       — real listener plays only
 *   🏆 Reach      — promotional exposure
 *   👁 Impressions — estimated discovery visibility
 *   Never combined into one number.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SharedNavigation from "@/components/SharedNavigation";
import { useCurrentUser } from "@/lib/useCurrentUser";
import LeaderboardPlacementShareModal from "@/components/LeaderboardPlacementShareModal";
import { optimizeImage } from "@/lib/mediaUrls";

const fmt = (n) => {
  const v = Math.round(Number(n) || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const MOVEMENT_META = {
  up:   { icon: "⬆", label: "Rising",     cls: "text-emerald-300 bg-emerald-500/15 border-emerald-400/40" },
  down: { icon: "⬇", label: "Falling",    cls: "text-red-300 bg-red-500/15 border-red-400/40" },
  flat: { icon: "🔥", label: "Holding",   cls: "text-amber-300 bg-amber-500/15 border-amber-400/40" },
  new:  { icon: "✨", label: "New entry", cls: "text-fuchsia-300 bg-fuchsia-500/15 border-fuchsia-400/40" },
};

const formatDayKey = (key) => {
  if (!key) return "";
  try {
    return new Date(`${key}T00:00:00Z`).toLocaleDateString(undefined, {
      weekday: "long", year: "numeric", month: "long", day: "numeric"
    });
  } catch { return key; }
};

const Medal = ({ rank }) => {
  const map = {
    1: { emoji: "🥇", from: "from-amber-300", to: "to-yellow-500", glow: "shadow-amber-500/40" },
    2: { emoji: "🥈", from: "from-slate-200", to: "to-slate-400", glow: "shadow-slate-400/30" },
    3: { emoji: "🥉", from: "from-orange-400", to: "to-amber-600", glow: "shadow-orange-500/30" },
  };
  const m = map[rank];
  if (!m) return null;
  return (
    <div className={`relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br ${m.from} ${m.to} text-3xl shadow-lg ${m.glow}`}>
      {m.emoji}
    </div>
  );
};

const PodiumCard = ({ row, height, accent }) => {
  return (
    <Link
      to={`/artist/${row.user_id}`}
      className={`group relative flex-1 flex flex-col items-center justify-end rounded-2xl border ${accent.border} bg-gradient-to-b ${accent.bg} hover:scale-[1.02] transition-transform`}
      style={{ height }}
      data-testid={`podium-rank-${row.rank}`}
    >
      <Medal rank={row.rank} />
      <div className="w-16 h-16 mt-3 rounded-full overflow-hidden border-2 border-amber-400/70 bg-amber-500/20">
        {row.artist_photo ? (
          <img src={optimizeImage(row.artist_photo, 'avatar')} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white font-black text-xl">
            {(row.artist_name || "?").charAt(0)}
          </div>
        )}
      </div>
      <p className="mt-2 text-white font-bold text-sm text-center px-2 truncate w-full">
        {row.artist_name}
      </p>
      <p className="text-amber-300 font-black text-xl mt-1">+{fmt(row.delta_24h)}</p>
      <p className="text-[9px] uppercase tracking-widest text-amber-300/70 mb-3">reach 24h</p>
    </Link>
  );
};

const LeaderboardRow = ({ row, onShare }) => {
  const mv = MOVEMENT_META[row.movement] || MOVEMENT_META.flat;
  return (
    <div
      className="grid grid-cols-[40px_56px_1fr_auto] items-center gap-3 px-3 sm:px-4 py-3 rounded-xl border border-amber-500/15 bg-gradient-to-r from-amber-900/10 to-black/40 hover:border-amber-500/40 transition-colors"
      data-testid={`leaderboard-row-${row.rank}`}
    >
      <div className="text-center">
        <div className="text-amber-200 font-black text-lg" data-testid={`leaderboard-rank-${row.rank}`}>
          #{row.rank}
        </div>
        {row.is_fastest_growing && (
          <div className="text-[9px] uppercase tracking-wider text-fuchsia-300 font-bold mt-0.5">
            🚀
          </div>
        )}
      </div>

      <Link to={`/artist/${row.user_id}`} className="block">
        <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-amber-400/30 bg-gradient-to-br from-fuchsia-600 to-purple-700">
          {row.artist_photo ? (
            <img src={optimizeImage(row.artist_photo, 'avatar')} alt="" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white font-black">
              {(row.artist_name || "?").charAt(0)}
            </div>
          )}
        </div>
      </Link>

      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/artist/${row.user_id}`} className="text-white font-bold text-sm sm:text-base hover:text-amber-200 truncate">
            {row.artist_name}
          </Link>
          <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${mv.cls}`} data-testid={`leaderboard-movement-${row.rank}`}>
            {mv.icon} {mv.label}
          </span>
          {row.spotlight_count > 0 && (
            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-purple-500/15 border border-purple-400/40 text-purple-200">
              🌟 ×{row.spotlight_count}
            </span>
          )}
          {row.featured_count > 0 && (
            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-400/40 text-amber-200">
              🎖 ×{row.featured_count}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 text-[11px] flex-wrap">
          <span className="text-amber-300" title="Boosted Reach (promotional exposure)">
            🏆 <span className="text-white font-bold">{fmt(row.total_reach)}</span> reach
          </span>
          <span className="text-pink-300" title="Estimated discovery impressions (reach × 8)">
            👁 <span className="text-white font-bold">{fmt(row.impressions)}</span> imp.
          </span>
          <span className="text-cyan-300" title="Real listener plays — never mixed with reach">
            ▶ <span className="text-white font-bold">{fmt(row.display_plays ?? row.real_plays)}</span> plays
          </span>
        </div>
      </div>

      <button
        onClick={() => onShare(row)}
        className="text-[10px] sm:text-xs px-2.5 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-pink-500 hover:from-amber-400 hover:to-pink-400 text-white font-bold whitespace-nowrap shadow-md shadow-amber-500/30"
        data-testid={`share-placement-btn-${row.rank}`}
      >
        🎖️ Share
      </button>
    </div>
  );
};

const PublicLeaderboardPage = () => {
  const { currentUser } = useCurrentUser();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareRow, setShareRow] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        const r = await api.get("/leaderboard/most-pushed-today?limit=25");
        setData(r.data);
      } catch (e) {
        setError("Couldn't load today's leaderboard. Try again in a moment.");
      } finally {
        setLoading(false);
      }
    };
    run();
    const id = setInterval(run, 90_000);
    return () => clearInterval(id);
  }, []);

  const top3 = (data?.leaderboard || []).slice(0, 3);
  const rest = (data?.leaderboard || []).slice(3);

  return (
    <div className="min-h-screen bg-black text-white">
      <SharedNavigation currentUser={currentUser} />

      <header className="max-w-5xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200 text-[10px] uppercase tracking-[3px] font-black">
            🏆 RGM Studio · Most Pushed Today
          </div>
          <h1 className="mt-4 text-3xl sm:text-5xl font-black bg-gradient-to-r from-amber-300 via-pink-300 to-purple-300 bg-clip-text text-transparent">
            Today's Top 25
          </h1>
          <p className="mt-2 text-sm sm:text-base text-amber-100/70 max-w-xl mx-auto">
            Ranked by <span className="text-white font-bold">🏆 boosted reach growth</span> in the last 24 hours.
            Refreshes every 90 seconds.
          </p>
          <p className="mt-1 text-[11px] uppercase tracking-widest text-amber-300/60" data-testid="leaderboard-day-key">
            {formatDayKey(data?.day_key)}
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-20">
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-amber-900/10 border border-amber-500/15 animate-pulse" />
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="p-6 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 text-center">
            {error}
          </div>
        )}

        {!loading && !error && data && data.leaderboard.length === 0 && (
          <div className="p-10 rounded-2xl bg-gradient-to-br from-amber-900/15 to-black border border-amber-500/20 text-center">
            <div className="text-5xl mb-3">🌙</div>
            <p className="text-white font-bold">No artists pushing yet today.</p>
            <p className="text-sm text-amber-200/70 mt-1">
              Featured tracks are waking up. Check back in a few hours — the leaderboard
              fills as RGM Studio boosts more tracks throughout the UTC day.
            </p>
          </div>
        )}

        {!loading && !error && data && data.leaderboard.length > 0 && (
          <>
            {/* Animated podium — pulled to silver/gold/bronze stage */}
            {top3.length > 0 && (
              <section
                className="grid grid-cols-3 gap-2 sm:gap-4 items-end mb-8"
                data-testid="leaderboard-podium"
              >
                {/* 2nd, 1st, 3rd ordering for classic podium feel */}
                {top3[1] && (
                  <PodiumCard
                    row={top3[1]}
                    height={210}
                    accent={{ border: "border-slate-300/50", bg: "from-slate-400/25 via-black to-black" }}
                  />
                )}
                {top3[0] && (
                  <PodiumCard
                    row={top3[0]}
                    height={260}
                    accent={{ border: "border-amber-300/70", bg: "from-amber-400/30 via-amber-900/20 to-black" }}
                  />
                )}
                {top3[2] && (
                  <PodiumCard
                    row={top3[2]}
                    height={190}
                    accent={{ border: "border-orange-400/50", bg: "from-orange-500/25 via-black to-black" }}
                  />
                )}
              </section>
            )}

            {/* Rows 4..25 */}
            <div className="space-y-2" data-testid="leaderboard-rows">
              {rest.map((row) => (
                <LeaderboardRow key={row.user_id} row={row} onShare={setShareRow} />
              ))}
              {/* Top 3 rows also appear below the podium with full stats + share */}
              {top3.length > 0 && (
                <details className="mt-6">
                  <summary className="text-xs text-amber-300/80 cursor-pointer hover:text-amber-200 select-none px-2">
                    Show full stats + share for top 3 →
                  </summary>
                  <div className="mt-2 space-y-2">
                    {top3.map((row) => (
                      <LeaderboardRow key={`top-${row.user_id}`} row={row} onShare={setShareRow} />
                    ))}
                  </div>
                </details>
              )}
            </div>

            <p className="mt-10 text-center text-[10px] uppercase tracking-widest text-amber-300/40">
              ▶ Plays = real listener plays · 🏆 Reach = promotional exposure ·
              👁 Impressions = estimated discovery visibility ·
              never mixed into a single number.
            </p>
          </>
        )}
      </main>

      <LeaderboardPlacementShareModal
        open={!!shareRow}
        onOpenChange={(o) => { if (!o) setShareRow(null); }}
        row={shareRow}
        dayKey={data?.day_key}
      />
    </div>
  );
};

export default PublicLeaderboardPage;
